/**
 * The TAQON brand, as on taqon.ai: charcoal surfaces, a mint accent and Montserrat. Mint is too
 * light to read as text on white (2:1), so text, icons and outlines in the brand colour use
 * `ink`, and anything written on mint is charcoal (FRD-CORE-041; pinned by `theme-contrast.test`).
 */
export const BRAND = {
  charcoal: '#2f3136',
  charcoalDark: '#1e2023',
  charcoalLight: '#3d4147',
  mint: '#6ccb9a',
  mintLight: '#8dd9b1',
  mintDark: '#52b882',
  /** The brand green as text, icons and outlines on light surfaces. */
  ink: '#1a6e49',
  /** Text and icons on mint. */
  onMint: '#1e2023',
} as const;

/** Under white text: the page banner. */
export const SURFACE_STOPS = [BRAND.charcoalDark, BRAND.charcoal, BRAND.charcoalLight] as const;
/** Under charcoal text: primary buttons, the active tab and the active menu entry. */
export const ACCENT_STOPS = [BRAND.mint, BRAND.mintDark] as const;

export function surfaceGradient(angle = '135deg'): string {
  return `linear-gradient(${angle}, ${SURFACE_STOPS.join(', ')})`;
}

export function accentGradient(angle = '135deg'): string {
  return `linear-gradient(${angle}, ${ACCENT_STOPS.join(', ')})`;
}

export const MINT_GLOW = 'rgba(108, 203, 154, 0.15)';
export const MINT_GLOW_STRONG = 'rgba(108, 203, 154, 0.3)';
/** The lift under a mint control. */
export const ACCENT_SHADOW = '0 10px 20px -10px rgba(82, 184, 130, 0.75)';

/** Card radius; controls (buttons, fields, tabs) use the theme's 12px. */
export const CARD_RADIUS = 16;
export const SHADOW_LG =
  '0 10px 15px -3px rgba(47, 49, 54, 0.08), 0 4px 6px -4px rgba(47, 49, 54, 0.06)';
export const BORDER = 'rgba(47, 49, 54, 0.10)';

/**
 * Colour accents beside the brand: neighbouring items on a page (sidebar entries, stat cards,
 * dashboard cards) each take the next pair, so a page shows many colours while banners, buttons and
 * the active entry keep the charcoal and mint. Each pair is a light and a dark stop: white icons sit
 * on both (3:1 for a meaningful graphic, WCAG 1.4.11), and the dark stop is also used as text on
 * white (4.5:1). Both pinned by `theme-contrast.test`.
 */
export const COLOUR_CYCLE: readonly (readonly [string, string])[] = [
  ['#3b82f6', '#1d4ed8'],
  ['#059669', '#047857'],
  ['#f43f5e', '#be123c'],
  ['#d97706', '#b45309'],
  ['#0891b2', '#0e7490'],
  ['#ec4899', '#be185d'],
  ['#ea580c', '#c2410c'],
  ['#6366f1', '#4338ca'],
];

/**
 * Styles for a container whose children take the colour cycle in turn, through the custom
 * properties `--cycle-light`, `--cycle-dark` and `--cycle-icon` (`selector` picks the children).
 */
export function colourCycle(selector = '& > *'): Record<string, Record<string, string>> {
  return Object.fromEntries(
    COLOUR_CYCLE.map(([light, dark], index) => [
      `${selector}:nth-of-type(${String(COLOUR_CYCLE.length)}n + ${String(index + 1)})`,
      { '--cycle-light': light, '--cycle-dark': dark, '--cycle-icon': '#ffffff' },
    ]),
  );
}

/** A rainbow for decoration only: the line under the header. */
export const RAINBOW =
  'linear-gradient(90deg, #2563eb, #4f46e5, #c026d3, #e11d48, #f59e0b, #6ccb9a, #06b6d4)';
